import { useMemo, useState } from 'react';
import { useLocation } from 'wouter';
import AccessDenied from '@/components/AccessDenied';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useAuth } from '@/hooks/useAuth';
import {
  useCreateQuote,
  type CreateQuoteDto,
  type QuoteItemInputDto,
} from '@/hooks/useQuoteContract';
import {
  QUOTE_CONTRACT_PERMISSIONS,
  hasPermission,
} from '@/lib/quoteContractPermissions';

const DECIMAL_REGEX = /^\d{1,18}(\.\d{1,6})?$/;

interface ItemDraft {
  category: string;
  name: string;
  description: string;
  quantity: string;
  unit: string;
  listUnitPrice: string;
  discountAmount: string;
  finalUnitPrice: string;
  giftCost: string;
  marginAmount: string;
}

const EMPTY_ITEM: ItemDraft = {
  category: '',
  name: '',
  description: '',
  quantity: '1',
  unit: 'set',
  listUnitPrice: '0',
  discountAmount: '0',
  finalUnitPrice: '0',
  giftCost: '0',
  marginAmount: '0',
};

function useSearchParam(name: string): string {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get(name) ?? '';
}

export default function QuoteCreatePage() {
  const [, navigate] = useLocation();
  const { permissions } = useAuth();
  const canCreate = hasPermission(
    permissions,
    QUOTE_CONTRACT_PERMISSIONS.CREATE,
  );
  const { createQuote, loading, error } = useCreateQuote();

  const initialProposalId = useSearchParam('proposalId');
  const initialProposalVersionId = useSearchParam('proposalVersionId');

  const [proposalId, setProposalId] = useState(initialProposalId);
  const [proposalVersionId, setProposalVersionId] = useState(
    initialProposalVersionId,
  );
  const [title, setTitle] = useState('');
  const [currency, setCurrency] = useState('CNY');
  const [notes, setNotes] = useState('');
  const [ownerUserId, setOwnerUserId] = useState('');
  const [items, setItems] = useState<ItemDraft[]>([{ ...EMPTY_ITEM }]);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const itemsValid = useMemo(() => {
    if (items.length === 0) return false;
    return items.every(
      (item) =>
        item.category.trim().length > 0 &&
        item.name.trim().length > 0 &&
        item.unit.trim().length > 0 &&
        DECIMAL_REGEX.test(item.quantity) &&
        DECIMAL_REGEX.test(item.listUnitPrice) &&
        DECIMAL_REGEX.test(item.discountAmount) &&
        DECIMAL_REGEX.test(item.finalUnitPrice) &&
        DECIMAL_REGEX.test(item.giftCost),
    );
  }, [items]);

  if (!canCreate) return <AccessDenied resource="报价新建" />;

  const updateItem = (index: number, patch: Partial<ItemDraft>) => {
    setItems((current) =>
      current.map((item, i) => (i === index ? { ...item, ...patch } : item)),
    );
  };

  const removeItem = (index: number) => {
    setItems((current) => current.filter((_, i) => i !== index));
  };

  const handleSubmit = async () => {
    setSubmitError(null);
    if (!proposalId.trim() || !proposalVersionId.trim()) {
      setSubmitError('方案 ID 和方案版本 ID 必填，且方案必须已审核通过或锁定');
      return;
    }
    if (!title.trim()) {
      setSubmitError('报价标题必填');
      return;
    }
    if (!itemsValid) {
      setSubmitError('请检查报价明细：分类、名称、单位、金额格式必须正确');
      return;
    }
    const dto: CreateQuoteDto = {
      proposalId: proposalId.trim(),
      proposalVersionId: proposalVersionId.trim(),
      title: title.trim(),
      currency: currency.trim() || 'CNY',
      notes: notes.trim() || undefined,
      ownerUserId: ownerUserId.trim() || undefined,
      items: items.map<QuoteItemInputDto>((item) => ({
        category: item.category.trim(),
        name: item.name.trim(),
        description: item.description.trim() || undefined,
        quantity: item.quantity,
        unit: item.unit.trim(),
        listUnitPrice: item.listUnitPrice,
        discountAmount: item.discountAmount,
        finalUnitPrice: item.finalUnitPrice,
        giftCost: item.giftCost,
        marginAmount: item.marginAmount,
      })),
    };
    const created = await createQuote(dto);
    if (created) {
      navigate(`/quote-contract/quotes/${created.id}`);
    }
  };

  return (
    <div className="space-y-4 p-6">
      <div>
        <h1 className="text-xl font-semibold">新建正式报价</h1>
        <p className="text-sm text-muted-foreground">
          报价必须基于已审核通过或已锁定的方案版本，金额字段使用 decimal 字符串。
        </p>
      </div>

      <Card className="space-y-3 p-4">
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="space-y-1">
            <Label htmlFor="proposalId">方案 ID</Label>
            <Input
              id="proposalId"
              value={proposalId}
              onChange={(e) => setProposalId(e.target.value)}
              placeholder="prop_..."
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="proposalVersionId">方案版本 ID</Label>
            <Input
              id="proposalVersionId"
              value={proposalVersionId}
              onChange={(e) => setProposalVersionId(e.target.value)}
              placeholder="pv_..."
            />
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label htmlFor="title">报价标题</Label>
            <Input
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="例如：客户 A 细胞健康方案报价 v1"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="currency">币种</Label>
            <Input
              id="currency"
              value={currency}
              onChange={(e) => setCurrency(e.target.value)}
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="owner">负责顾问 (默认为当前账号)</Label>
            <Input
              id="owner"
              value={ownerUserId}
              onChange={(e) => setOwnerUserId(e.target.value)}
              placeholder="usr_..."
            />
          </div>
          <div className="space-y-1 md:col-span-2">
            <Label htmlFor="notes">备注</Label>
            <textarea
              id="notes"
              className="w-full rounded-md border bg-background px-3 py-2 text-sm"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
        </div>
      </Card>

      <Card className="space-y-3 p-4">
        <div className="flex items-center justify-between">
          <h2 className="font-medium">报价明细</h2>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() =>
              setItems((current) => [...current, { ...EMPTY_ITEM }])
            }
          >
            添加一行
          </Button>
        </div>
        {items.map((item, index) => (
          <Card key={index} className="space-y-3 p-3">
            <div className="grid grid-cols-1 gap-2 md:grid-cols-4">
              <div className="space-y-1">
                <Label>分类</Label>
                <Input
                  value={item.category}
                  onChange={(e) =>
                    updateItem(index, { category: e.target.value })
                  }
                  placeholder="cell_product / lab_service / addon"
                />
              </div>
              <div className="space-y-1">
                <Label>名称</Label>
                <Input
                  value={item.name}
                  onChange={(e) => updateItem(index, { name: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label>单位</Label>
                <Input
                  value={item.unit}
                  onChange={(e) => updateItem(index, { unit: e.target.value })}
                />
              </div>
              <div className="space-y-1">
                <Label>数量</Label>
                <Input
                  value={item.quantity}
                  onChange={(e) =>
                    updateItem(index, { quantity: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>定价单价</Label>
                <Input
                  value={item.listUnitPrice}
                  onChange={(e) =>
                    updateItem(index, { listUnitPrice: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>折扣</Label>
                <Input
                  value={item.discountAmount}
                  onChange={(e) =>
                    updateItem(index, { discountAmount: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>实际单价</Label>
                <Input
                  value={item.finalUnitPrice}
                  onChange={(e) =>
                    updateItem(index, { finalUnitPrice: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>赠送成本</Label>
                <Input
                  value={item.giftCost}
                  onChange={(e) =>
                    updateItem(index, { giftCost: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1">
                <Label>毛利</Label>
                <Input
                  value={item.marginAmount}
                  onChange={(e) =>
                    updateItem(index, { marginAmount: e.target.value })
                  }
                />
              </div>
              <div className="space-y-1 md:col-span-3">
                <Label>说明</Label>
                <Input
                  value={item.description}
                  onChange={(e) =>
                    updateItem(index, { description: e.target.value })
                  }
                />
              </div>
            </div>
            {items.length > 1 && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeItem(index)}
              >
                删除该行
              </Button>
            )}
          </Card>
        ))}
      </Card>

      {(submitError || error) && (
        <Card className="bg-destructive/10 p-3 text-sm text-destructive">
          {submitError ?? error?.message}
        </Card>
      )}

      <div className="flex gap-3">
        <Button onClick={handleSubmit} disabled={loading}>
          {loading ? '创建中…' : '创建草稿'}
        </Button>
        <Button
          variant="ghost"
          onClick={() => navigate('/quote-contract/quotes')}
        >
          返回列表
        </Button>
      </div>
    </div>
  );
}
